import React from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { Button, ButtonColor, ButtonVariant } from '@makyoapp/makyo-ui'

import { useBilling } from '../Providers/BillingProviders'

interface PlanFeatures {
  publishedApps: string
  rows: string
  storage: string
  appDrafts: string
  members: string
  teams: string
  brandKits: string
  databases: string
  collaborations: string
  customDomains: string
  makyoBranding: string
  [key: string]: string // Index signature for dynamic access
}

const BillingPlansPage = () => {
  const { billingCycle, currentPlan } = useBilling()
  const { t } = useTranslation()

  // Feature categories for the plan comparison with icons
  // Feature categories for the plan comparison with icons
  const featureCategories = [
    {
      id: 'publishedApps',
      name: 'Published Apps',
      icon: (
        <svg
          className='h-5 w-5 text-teal-500 mr-2 fill-transparent'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
          />
        </svg>
      ),
    },
    {
      id: 'rows',
      name: 'Rows (Drafts included)',
      icon: (
        <svg
          className='h-5 w-5 text-blue-500 mr-2 fill-transparent'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7c-2 0-3 1-3 3zm0 5h16'
          />
        </svg>
      ),
    },
    {
      id: 'storage',
      name: 'Storage',
      icon: (
        <svg
          className='h-5 w-5 text-purple-500 mr-2 fill-transparent'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7c-2 0-3 1-3 3zm0 5h16M12 4v16'
          />
        </svg>
      ),
    },
    {
      id: 'appDrafts',
      name: 'App Drafts',
      icon: (
        <svg
          className='h-5 w-5 text-amber-500 mr-2 fill-transparent'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
          />
        </svg>
      ),
    },
    {
      id: 'members',
      name: 'Members',
      icon: (
        <svg
          className='h-5 w-5 text-green-500 mr-2 fill-transparent'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M16 7a4 4 0 11-8 0 4 4 0 018 0zm-4 7a7 7 0 00-7 7h14a7 7 0 00-7-7z'
          />
        </svg>
      ),
    },
    {
      id: 'teams',
      name: 'Teams',
      icon: (
        <svg
          className='h-5 w-5 text-indigo-500 mr-2 fill-transparent'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z'
          />
        </svg>
      ),
    },
    {
      id: 'brandKits',
      name: 'Brand kits',
      icon: (
        <svg
          className='h-5 w-5 text-pink-500 mr-2 fill-transparent'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01'
          />
        </svg>
      ),
    },
    {
      id: 'databases',
      name: 'Databases',
      icon: (
        <svg
          className='h-5 w-5 text-yellow-500 mr-2 fill-transparent'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7c-2 0-3 1-3 3z'
          />
        </svg>
      ),
    },
    {
      id: 'collaborations',
      name: 'Collaborations',
      icon: (
        <svg
          className='h-5 w-5 text-red-500 mr-2 fill-transparent'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122'
          />
        </svg>
      ),
    },
    {
      id: 'customDomains',
      name: 'Custom domains',
      icon: (
        <svg
          className='h-5 w-5 text-orange-500 mr-2 fill-transparent'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9'
          />
        </svg>
      ),
    },
    {
      id: 'makyoBranding',
      name: 'Makyo Branding',
      icon: (
        <svg
          className='h-5 w-5 text-gray-500 mr-2 fill-transparent'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 20l4-16m2 16l4-16M6 9h14M4 15h14' />
        </svg>
      ),
    },
  ]

  // Plans data
  const plans: Array<{
    id: string
    name: string
    monthlyPrice: number
    yearlyPrice: number
    popular?: boolean
    features: PlanFeatures
  }> = [
    {
      id: 'free',
      name: 'Free',
      monthlyPrice: 0,
      yearlyPrice: 0,
      features: {
        publishedApps: '1',
        rows: '25,000',
        storage: '1 G',
        appDrafts: 'Unlimited',
        members: '1',
        teams: '1',
        brandKits: '1',
        databases: '1',
        collaborations: 'Not included',
        customDomains: 'No',
        makyoBranding: 'Yes',
      },
    },
    {
      id: 'creator',
      name: 'Creator',
      monthlyPrice: 59,
      yearlyPrice: 590,
      popular: true,
      features: {
        publishedApps: '3',
        rows: '50,000',
        storage: '3 G',
        appDrafts: 'Unlimited',
        members: '3',
        teams: '3',
        brandKits: '3',
        databases: '3',
        collaborations: 'Included',
        customDomains: 'Yes',
        makyoBranding: 'No',
      },
    },
    {
      id: 'agency',
      name: 'Agency',
      monthlyPrice: 260,
      yearlyPrice: 2600,
      features: {
        publishedApps: '20',
        rows: '100,000',
        storage: '13 G',
        appDrafts: 'Unlimited',
        members: '30',
        teams: 'Unlimited',
        brandKits: 'Unlimited',
        databases: 'Unlimited',
        collaborations: 'Included',
        customDomains: 'Yes',
        makyoBranding: 'No',
      },
    },
  ]

  return (
    <div className='mx-auto -mt-4'>
      <div className='bg-white dark:bg-gray-800 rounded-xl shadow-xs border border-gray-200 dark:border-gray-700 overflow-hidden mb-8'>
        <div className='overflow-x-auto'>
          <table className='w-full'>
            <thead>
              <tr className='bg-gray-50 dark:bg-slate-800'>
                <th className='py-4 px-6 text-left font-medium text-gray-700 dark:text-gray-300 text-base'>
                  {t('Feature')}
                </th>
                {plans.map((plan, index) => (
                  <th key={plan.id} className={clsx('py-4 px-6 text-center font-medium')}>
                    <div
                      className={clsx(
                        'flex flex-col items-center rounded-lg p-3',
                        index === 0 ? 'bg-gray-400/10 dark:bg-gray-500/30' : '',
                        plan.popular ? 'bg-blue-50 dark:bg-blue-900/30 shadow-md' : '',
                        index === plans.length - 1
                          ? 'bg-linear-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20'
                          : ''
                      )}
                    >
                      <span
                        className={clsx(
                          'text-lg font-semibold',
                          index === 0 ? 'text-gray-500 dark:text-gray-400' : '',
                          plan.popular ? 'text-blue-600 dark:text-blue-300' : '',
                          index === plans.length - 1 ? 'text-purple-700 dark:text-purple-300' : '',
                          !plan.popular && index !== 0 && index !== plans.length - 1
                            ? 'text-gray-900 dark:text-white'
                            : ''
                        )}
                      >
                        {plan.name}
                      </span>
                      <span className='mt-1 text-2xl font-bold text-gray-900 dark:text-white min-w-[150px]'>
                        {billingCycle === 'yearly' ? `$${plan.yearlyPrice}/year` : `$${plan.monthlyPrice}/mo`}
                      </span>

                      {index === 0 && (
                        <span className='mt-2 px-2 py-0.5 bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-full text-xs font-medium'>
                          {t('Try Free')}
                        </span>
                      )}

                      {plan.popular && (
                        <span className='mt-2 px-2 py-0.5 bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-100 rounded-full text-xs font-medium'>
                          {t('Most Popular')}
                        </span>
                      )}

                      {index === plans.length - 1 && (
                        <span className='mt-2 px-2 py-0.5 bg-purple-100 dark:bg-purple-800 text-purple-800 dark:text-purple-100 rounded-full text-xs font-medium'>
                          {t('Premium')}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {featureCategories.map((feature, index) => (
                <tr
                  key={feature.id}
                  className={clsx(index % 2 === 0 ? 'bg-white dark:bg-slate-900' : 'bg-gray-50 dark:bg-slate-800')}
                >
                  <td className='py-4 px-6 font-medium text-gray-900 dark:text-white'>
                    <div className='flex items-center gap-2'>
                      {feature.icon}
                      <span>{feature.name}</span>
                    </div>
                  </td>
                  {plans.map((plan, planIndex) => (
                    <td
                      key={`${plan.id}-${feature.id}`}
                      className={clsx(
                        'py-4 px-6 text-center',
                        planIndex === 0 ? 'bg-gray-200/40 dark:bg-gray-800/20' : '',
                        plan.popular ? 'bg-blue-50/50 dark:bg-blue-900/10' : '',
                        planIndex === plans.length - 1 ? 'bg-purple-50/50 dark:bg-purple-900/10' : ''
                      )}
                    >
                      <div className='flex justify-center items-center'>
                        {plan.features[feature.id] === 'Not included' || plan.features[feature.id] === 'No' ? (
                          <svg
                            className='h-5 w-5 text-red-500 dark:text-red-400 fill-transparent'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M6 18L18 6M6 6l12 12'
                            />
                          </svg>
                        ) : plan.features[feature.id] === 'Yes' || plan.features[feature.id] === 'Included' ? (
                          <svg
                            className={clsx('h-5 w-5 fill-transparent text-green-500 dark:text-green-400')}
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                          </svg>
                        ) : (
                          <span className={clsx('font-medium', 'text-gray-900 dark:text-white')}>
                            {plan.features[feature.id]}
                          </span>
                        )}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
              <tr className='bg-gray-50 dark:bg-slate-800'>
                <td className='py-4 px-6'></td>
                {plans.map((plan, index) => {
                  // Determine if plan is a higher or lower tier than current plan
                  const currentPlanIndex = plans.findIndex((p) => p.name === currentPlan?.name)
                  const isPlanHigherTier = index > currentPlanIndex
                  const isPlanLowerTier = index < currentPlanIndex

                  let buttonText = t('Select Plan')
                  if (plan.name === currentPlan?.name) {
                    buttonText = t('Cancel Plan')
                  } else if (isPlanHigherTier) {
                    buttonText = t(`Upgrade to ${plan.name}`)
                  } else if (isPlanLowerTier) {
                    buttonText = t(`Downgrade to ${plan.name}`)
                  } else {
                    buttonText = t(`Select ${plan.name}`)
                  }

                  return (
                    <td
                      key={`${plan.id}-action`}
                      className={clsx(
                        'py-6 px-6 text-center',
                        index === 0 ? 'bg-gray-100/50 dark:bg-gray-800/30' : '',
                        plan.popular ? 'bg-blue-50/50 dark:bg-blue-900/10' : '',
                        index === plans.length - 1 ? 'bg-purple-50/50 dark:bg-purple-900/10' : ''
                      )}
                    >
                      <Button
                        variant={plan.name === currentPlan?.name ? ButtonVariant.Outlined : ButtonVariant.Normal}
                        className={clsx(
                          'mx-auto px-6 py-2.5 font-medium shadow-xs transition-all',
                          index === 0 ? 'font-normal' : 'font-medium',
                          plan.popular ? 'hover:shadow-md' : '',
                          index === plans.length - 1 ? 'hover:shadow-lg' : ''
                        )}
                        colorStyle={plan.name === currentPlan?.name ? ButtonColor.Danger : ButtonColor.Primary}
                      >
                        {buttonText}
                      </Button>

                      {index === 0 && <p className='mt-2 text-xs text-gray-900 dark:text-white'>{t('For Testing')}</p>}

                      {plan.popular && (
                        <p className='mt-2 text-xs text-gray-900 dark:text-white'>{t('Best value for most users')}</p>
                      )}

                      {index === plans.length - 1 && (
                        <p className='mt-2 text-xs text-gray-900 dark:text-white'>{t('For power users & teams')}</p>
                      )}
                    </td>
                  )
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default BillingPlansPage
